/**
 * Сколько сообщений от игроков ждут ответа.
 *
 * Без счётчика раздел приходится открывать наугад: сообщения приходят редко,
 * и заходить туда «на всякий случай» модератор перестанет через неделю.
 *
 * Счётчик отдаёт сам эндпоинт списка и считает очередь целиком, а не текущий
 * фильтр: он висит на пункте меню, а не на таблице.
 */
import { useEffect, useState } from "react";

import { getAccessToken, request } from "../lib/http";

/** Как часто перечитывать: сообщения приходят редко, спешить некуда. */
const REFRESH_MS = 60_000;

/**
 * Пока не вошли, запрос отвечает 401. Ждать целую минуту после входа не
 * годится — счётчик появлялся бы у уже работающего модератора «сам собой»,
 * поэтому до первого успеха опрос идёт часто.
 */
const RETRY_MS = 3_000;

export function useNewFeedbackCount(): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let stopped = false;

    const load = async (): Promise<boolean> => {
      // До входа токена нет, и запрос заведомо отвечает 401 — не шумим им
      // в консоли и в журналах сервера.
      if (!getAccessToken()) return false;

      try {
        // Сам список не нужен — берём минимальную страницу ради счётчика.
        const data = await request<{ newCount?: number }>("/api/v1/admin/feedback", {
          query: { "page.pageSize": 1 },
        });
        if (!stopped) setCount(data.newCount ?? 0);
        return true;
      } catch {
        // Счётчик — не повод показывать ошибку: раздел открывается и без него.
        return false;
      }
    };

    let timer = 0;

    const schedule = (delay: number) => {
      timer = window.setTimeout(async () => {
        const ok = await load();
        if (!stopped) schedule(ok ? REFRESH_MS : RETRY_MS);
      }, delay);
    };

    void load().then((ok) => {
      if (!stopped) schedule(ok ? REFRESH_MS : RETRY_MS);
    });

    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, []);

  return count;
}
